import {
  ScreenBundle,
  ScreenBundleDoc,
} from '../../interfaces/app-ui/screen-bundle.interface';
import { BundleSyncService } from './bundle-sync.service';
import { DB_KEY_PREFIXES } from '../../config/pouch-db.config';
import { EntityNames } from '../delta/delta.service';
import { Injectable } from '@angular/core';

const SCREEN_BUNDLE_ACTION = 'indskr_GetScreenBundle';

/**
 * App Studio screen configuration from `indskr_GetScreenBundle`, one document per screen key.
 * A feature syncs its own screens. Not the `indskr_mobileappform` sync: that one inner-joins
 * roles, which a screen may not have.
 */
@Injectable({ providedIn: 'root' })
export class AppUiScreenDataService {
  constructor(private readonly bundles: BundleSyncService) {}

  public getProjectionKey(screenKey: string): string {
    return `${DB_KEY_PREFIXES.APP_UI_SCREEN}_${screenKey}`;
  }

  public async getCachedBundle(
    screenKey: string,
  ): Promise<ScreenBundleDoc | null> {
    return this.bundles.cached<ScreenBundleDoc>(
      this.getProjectionKey(screenKey),
    );
  }

  /** `screenKey` is always sent: without it the server answers with the entity's first published layout. */
  public async syncScreenBundle(
    screenKey: string,
    entity: string,
    loadFromDbOnly = false,
  ): Promise<boolean> {
    if (loadFromDbOnly || this.bundles.isOffline) return false;
    const key = this.getProjectionKey(screenKey);
    return this.bundles.sync<ScreenBundle, ScreenBundleDoc>({
      action: SCREEN_BUNDLE_ACTION,
      entityName: EntityNames.screenBundle,
      key,
      rawData: () => ({ screenKey, configKeys: [screenKey], entity }),
      stored: { screenKey, entity },
      // This action alone may answer "unchanged" with no payload.
      keepOnSuppressed: true,
      validate: (bundle) => assertUsable(bundle, key, screenKey),
      afterWrite: (bundle) => {
        if (bundle.layoutSource && bundle.layoutSource !== 'screenKey') {
          console.warn(
            `AppUiScreenDataService: ${key} resolved by ${bundle.layoutSource}, not screenKey. ${bundle.layoutDiagnostic ?? ''}`,
          );
        }
      },
    });
  }
}

/** Throws unless the bundle can render. Anything short is worse than no config. */
function assertUsable(
  bundle: ScreenBundle,
  key: string,
  screenKey: string,
): void {
  // Layout without metadata means labelless controls and empty dropdowns.
  if (!bundle.layout || !bundle.formMetadata?.Tabs?.length) {
    throw new Error(
      `Screen bundle for ${key} is missing layout or formMetadata`,
    );
  }
  // A different screen than we asked for must not be cached under our key.
  if (bundle.layoutKey && bundle.layoutKey !== screenKey) {
    throw new Error(
      `Screen bundle for ${key} answered with layoutKey ${bundle.layoutKey}`,
    );
  }
}
